import { apiClient } from './client'

/**
 * LGPD Block F1 — billing foundation client (provider-neutral, disabled).
 *
 * No card data is ever collected in the UI: any real payment entry must redirect
 * to a provider checkout/portal (Block F2). In F1 the write endpoints return a
 * disabled-safe response, which the UI renders as "billing not configured".
 */

export interface BillingPlanDTO {
  code: string
  name: string
  description: string | null
  price_cents: number | null
  currency: string
  billing_interval: string
  is_active: boolean
  limits: Record<string, unknown> | null
}

export interface BillingPlansDTO {
  total: number
  items: BillingPlanDTO[]
}

export interface BillingCustomerDTO {
  provider: string
  billing_email: string | null
  billing_name: string | null
  billing_country: string | null
}

export interface BillingSubscriptionDTO {
  status: string
  provider: string
  plan_code: string | null
  plan_name: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  trial_end: string | null
}

export interface BillingEntitlementsDTO {
  plan_code: string
  enforcement_enabled: boolean
  limits: Record<string, unknown>
  usage: Record<string, number>
  checks: Record<string, boolean>
}

export interface BillingSummaryDTO {
  enabled: boolean
  provider: string
  plan: BillingPlanDTO | null
  subscription: BillingSubscriptionDTO | null
  customer: BillingCustomerDTO | null
  entitlements: BillingEntitlementsDTO
}

export interface CheckoutResponseDTO {
  enabled: boolean
  checkout_url: string | null
  detail: string
}

export interface PortalResponseDTO {
  enabled: boolean
  portal_url: string | null
  detail: string
}

export interface CancelResponseDTO {
  status: string
  cancel_at_period_end: boolean
  detail: string
}

export const billingApi = {
  getPlans: (signal?: AbortSignal) =>
    apiClient.get<BillingPlansDTO>('/api/v1/billing/plans', undefined, signal),

  getMe: (signal?: AbortSignal) =>
    apiClient.get<BillingSummaryDTO>('/api/v1/billing/me', undefined, signal),

  checkout: (planCode: string, interval?: string) =>
    apiClient.post<CheckoutResponseDTO>('/api/v1/billing/checkout', {
      plan_code: planCode,
      ...(interval ? { interval } : {}),
    }),

  portal: () => apiClient.post<PortalResponseDTO>('/api/v1/billing/portal'),

  cancel: () => apiClient.post<CancelResponseDTO>('/api/v1/billing/cancel'),
}
